/**
 * @file UsersPlansTab.tsx
 * @brief Super Admin → Analytics → Users & plans (W22), from B25 `saas`.
 *
 * @details
 * Sign-ups, conversion (self sign-ups only, mature cohorts only), churn,
 * ARPA, users by plan (Free split, paid plans, Enterprise) and the MRR
 * build-up. Churn before 21-10-2026 and an unready conversion cohort are
 * "–" with the API's reason, never 0%.
 *
 * @author DES (prepared with Claude)
 * @date 25-10-2026
 */

import { useMemo, type ReactNode } from "react";
import { GridCell, GridCells } from "@/features/analytics/components/GridBlock";
import { SkeletonRows } from "@/components/ui/Skeleton";
import { InfoTip } from "@/components/ui/InfoTip";
import {
  formatCount,
  formatMoney,
  formatPct,
  notConvertedLines,
  NULL_REASONS,
  periodLabel,
  pick,
  planLabel,
} from "../businessMath";
import type { Currency, Rate, Saas } from "../types";
import { BarsOverTime, type ChartSeries } from "../components/charts";
import { DataTable, ErrorNote, MoneyText, NullValue, SectionHeading, tdClass, tdNumClass } from "../components/common";

const SIGNUPS: ChartSeries[] = [{ key: "signups", label: "Sign-ups", color: "var(--viz-1)" }];

function Stat({ label, value, hint, info }: { label: string; value: ReactNode; hint?: ReactNode; info?: string }) {
  return (
    <GridCell>
      <p className="flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        {info ? <InfoTip label={label} text={info} /> : null}
      </p>
      <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
      {hint ? <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p> : null}
    </GridCell>
  );
}

function rateValue(r: Rate | undefined) {
  if (!r) return "–";
  return r.rate_pct == null ? <NullValue reason={NULL_REASONS.conversion} /> : formatPct(r.rate_pct);
}

export function UsersPlansTab({
  data,
  isLoading,
  error,
  currency,
}: {
  data: Saas | undefined;
  isLoading: boolean;
  error: unknown;
  currency: Currency;
}) {
  const rows = useMemo(
    () => (data?.signups.series ?? []).map((s) => ({ label: periodLabel(s.period, data!.window.groupBy), signups: s.signups })),
    [data],
  );

  if (error) return <ErrorNote error={error} what="users and plans" />;
  if (isLoading || !data) {
    return (
      <GridCell>
        <SkeletonRows />
      </GridCell>
    );
  }

  const c = data.conversion;
  const ch = data.churn;
  const free = data.users_by_plan.free;
  const ent = data.users_by_plan.enterprise;
  const otherMrr = notConvertedLines(data.mrr);

  return (
    <>
      <GridCells className="grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Stat label="Sign-ups" value={formatCount(data.signups.total)} hint="In this period; ours and Super Admins left out" />
        <Stat
          label="Converted in 7 days"
          value={rateValue(c.within_7_days)}
          hint={`${formatCount(c.within_7_days.converted)} of ${formatCount(c.within_7_days.mature)} old enough`}
          info={c.definition}
        />
        <Stat
          label="Converted in 30 days"
          value={rateValue(c.within_30_days)}
          hint={`${formatCount(c.within_30_days.converted)} of ${formatCount(c.within_30_days.mature)} old enough`}
          info={c.definition}
        />
        <Stat
          label="Churn"
          value={ch.rate_pct == null ? <NullValue reason={ch.reason ?? NULL_REASONS.churn_default} /> : formatPct(ch.rate_pct)}
          hint={
            ch.cancelled == null
              ? "Not recorded for this whole period"
              : `${formatCount(ch.cancelled)} ended of ${formatCount(ch.paying_accounts_before_window_still_paying + ch.cancelled)}`
          }
          info={ch.definition}
        />
        <Stat
          label="MRR per paying account"
          value={
            data.arpu.arpa_mrr ? (
              <MoneyText minor={pick(data.arpu.arpa_mrr, currency)} currency={currency} />
            ) : (
              <NullValue reason={data.mrr_reason ?? NULL_REASONS.mrr_default} />
            )
          }
          info={data.arpu.definition}
        />
        <Stat
          label="Revenue per paying customer"
          value={
            data.arpu.revenue_per_paying_customer ? (
              <MoneyText minor={pick(data.arpu.revenue_per_paying_customer, currency)} currency={currency} />
            ) : (
              <NullValue reason="Nobody paid anything in this period." />
            )
          }
          hint={
            data.arpu.revenue_per_paying_customer
              ? `${formatCount(data.arpu.revenue_per_paying_customer.customers)} paying customers in this period`
              : undefined
          }
          info={data.arpu.definition}
        />
      </GridCells>

      <GridCell>
        <SectionHeading
          title="Sign-ups"
          help={`Per ${data.window.groupBy}. Conversion counts self sign-ups only (no company, or a company admin); invited employees are not in the cohort (${formatCount(c.cohort)} in this period).`}
        />
        <div className="mt-3">
          <BarsOverTime
            data={rows}
            series={SIGNUPS}
            format={(v) => formatCount(v)}
            tick={(v) => formatCount(v)}
            isEmpty={data.signups.total === 0}
            emptyText="No sign-ups in this period."
            height={220}
            counts
          />
        </div>
      </GridCell>

      <GridCell padded={false}>
        <div className="px-4 py-4 md:px-5">
          <SectionHeading
            title="Users by plan"
            help="Live now, not limited to the period."
            info={free.definition}
          />
        </div>
        <DataTable
          caption="Users by plan"
          head={[
            { label: "Plan" },
            { label: "Paying", align: "right" },
            { label: "Trialing", align: "right" },
            { label: "Seats", align: "right" },
          ]}
        >
          <tr className="border-t border-border/60 align-top">
            <td className={tdClass}>
              <span className="font-medium">Free</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {free.free_plan_enabled ? "On" : "Off"} · {formatCount(free.notes_minutes_limit)} Notes minutes{" "}
                {free.reset_mode === "one_time" ? "once" : "a month"}
                {free.reset_mode === "one_time" && free.expiry_days > 0 ? `, expire after ${free.expiry_days} days` : ""}
              </span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {formatCount(free.total)} users: {formatCount(free.active)} active, {formatCount(free.used_up)} used up,{" "}
                {formatCount(free.expired)} expired · {formatCount(free.with_usage)} with some use
              </span>
            </td>
            <td className={tdNumClass}>–</td>
            <td className={tdNumClass}>–</td>
            <td className={tdNumClass}>{formatCount(free.total)}</td>
          </tr>
          {data.users_by_plan.plans.map((p) => (
            <tr key={`${p.account_type}-${p.plan_code}-${p.billing_interval}`} className="border-t border-border/60">
              <td className={tdClass}>
                <span className="font-medium">{planLabel(p.plan_code)}</span>{" "}
                <span className="text-xs text-muted-foreground">
                  {p.account_type === "team" ? "company team plan" : "personal"} · {p.billing_interval === "year" ? "yearly" : "monthly"}
                </span>
              </td>
              <td className={tdNumClass}>{formatCount(p.paying)}</td>
              <td className={tdNumClass}>{formatCount(p.trialing)}</td>
              <td className={tdNumClass}>{formatCount(p.seats)}</td>
            </tr>
          ))}
          <tr className="border-t border-border/60 align-top">
            <td className={tdClass}>
              <span className="font-medium">Enterprise</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {ent ? `${formatCount(ent.companies)} ${ent.companies === 1 ? "company" : "companies"} · ${ent.note}` : "The Enterprise tables couldn't be read."}
              </span>
            </td>
            <td className={tdNumClass}>–</td>
            <td className={tdNumClass}>–</td>
            <td className={tdNumClass}>{ent ? `${formatCount(ent.seats_used)} of ${formatCount(ent.seat_limit)}` : "–"}</td>
          </tr>
          <tr className="border-t-2 border-border font-semibold">
            <td className={tdClass}>Paying (Stripe plans)</td>
            <td className={tdNumClass}>{formatCount(data.users_by_plan.paying_accounts)}</td>
            <td className={tdNumClass} />
            <td className={tdNumClass}>{formatCount(data.users_by_plan.paying_seats)}</td>
          </tr>
        </DataTable>
      </GridCell>

      <GridCell>
        <SectionHeading title="MRR" help={data.mrr_definition} />
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          <div>
            <dt className="text-xs text-muted-foreground">MRR</dt>
            <dd className="text-lg font-semibold">
              {data.mrr ? (
                <MoneyText minor={pick(data.mrr, currency)} currency={currency} />
              ) : (
                <NullValue reason={data.mrr_reason ?? NULL_REASONS.mrr_default} />
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">ARR</dt>
            <dd className="text-lg font-semibold">
              {data.arr ? (
                <MoneyText minor={pick(data.arr, currency)} currency={currency} />
              ) : (
                <NullValue reason={data.mrr_reason ?? NULL_REASONS.mrr_default} />
              )}
            </dd>
          </div>
          {data.mrr ? (
            <div className="col-span-2">
              <dt className="text-xs text-muted-foreground">By currency charged</dt>
              <dd className="text-sm tabular-nums">
                {Object.entries(data.mrr.by_currency)
                  .map(([cur, v]) => formatMoney(v, cur.toUpperCase()))
                  .join(" · ") || "–"}
                {otherMrr.length ? <span className="text-muted-foreground"> ({otherMrr.join(", ")} not converted)</span> : null}
              </dd>
            </div>
          ) : data.mrr_known_part ? (
            <div className="col-span-2">
              <dt className="text-xs text-muted-foreground">Known part only (not MRR)</dt>
              <dd className="text-sm tabular-nums">
                <MoneyText minor={pick(data.mrr_known_part, currency)} currency={currency} />
              </dd>
            </div>
          ) : null}
        </dl>
        {data.data_quality.mrr_unpriced.length ? (
          <div className="mt-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">Subscriptions without a readable price</p>
            <ul className="mt-1 space-y-0.5">
              {data.data_quality.mrr_unpriced.map((u) => (
                <li key={u.id}>
                  {u.account_type} <span className="font-mono">{u.stripe_price_id ?? "(no price id)"}</span>: {u.reason}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {data.data_quality.stripe_vs_db_mismatches.length ? (
          <div className="mt-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">Stripe and our database disagree</p>
            <ul className="mt-1 space-y-0.5">
              {data.data_quality.stripe_vs_db_mismatches.map((m, i) => (
                <li key={`${m.id}-${m.field}-${i}`}>
                  {m.field}: ours {m.db}, Stripe {m.stripe} <span className="font-mono">({m.id})</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </GridCell>
    </>
  );
}
